/* eslint-disable react/no-danger */
/* eslint-disable import/no-extraneous-dependencies */
import { TextInput, NumberInput, RangeSlider } from 'crux';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import at from 'lodash/at';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { updateAnswersArray } from '../redux/actions';
import { updateQuestionAnswerTimeStamp } from '../redux/actions/questions';
import {
  getLabelProps,
  getValidators,
  getWidth,
  getTooltipProps,
  getCSSVariables,
  getTimeStampData,
  getGroupQAriaLabel,
} from './utils';
import { QuestionDescription, QuestionText, InputWrapper, Wrapper } from './utils/styles';
import ActionButtons from '../Components/QuestionsLib/ActionButtons';
import { scrollQuestionIntoView, validateInnerHtml, getUTCTimestamp, isQuotePage } from '../utils';
import { getCityStateLookup } from '../util/externalServices';
import { USPS_API_START, USPS_API_COMPLETE } from '../redux/types';

const FakeInput = styled.div.attrs({
  tabIndex: '0',
})`
  position: absolute;
  left: -9999px;
`;

const Input = ({
  question,
  optionalText,
  theme,
  handleChange,
  isEditMode,
  sectionIndex,
  currentSectionAnswerList,
  currentSectionQuestionList,
  lastFocusedElementId,
  screenViewport,
  dispatchMultipleActions,
  role,
  uspsQuestions,
  triggerValidation,
  updateTimeStamp,
  meta,
  questionsArray,
  listIndex,
  dispatchUspsStart,
  dispatchUspsComplete,
  // updateAnswerArrayInSocket,
}) => {
  const mask = !!get(question, 'validations.mask.mask_char', false);
  const maskChar = get(question, 'validations.mask.mask_char', '');
  const actualMaskRef = useRef(question.response);
  const prevResponseRef = useRef('');

  // Setting the initial response for the field
  useEffect(() => {
    if (isEditMode || question.response) {
      prevResponseRef.current = question.response;
      handleChange(question.response, false, { hasMask: mask }, {}, true, false, false);
    }
  }, []);

  const {
    pageId,
    config: {
      theme: {
        pageOverrides,
        components,
        global: { colorScheme, errorMessageColor, actualThreshold },
      },
    },
  } = theme;

  const labelProps = getLabelProps('input', components, pageId, pageOverrides);
  const { questionTextColor, questionDescriptionColor } = get(theme, 'config.theme.global.text');
  const showOptionalText = get(theme, 'config.theme.global.showOptionalText', false);
  const themeConfig = pageOverrides[pageId] && pageOverrides[pageId].themeConfiguration;
  const sliderLabelStyles =
    screenViewport === 'mobile'
      ? pageOverrides[pageId] && pageOverrides[pageId].sliderLabelStyles
      : [];
  const buttonLabelProps = getLabelProps('button', components, pageId, pageOverrides);
  const questionLayout = get(question, 'properties.layout.value');
  const labelPosition = questionLayout === 'two' ? 'left' : labelProps.labelPosition;
  const screenReaderLabelQuestionId = get(question, 'screen_reader_label_id', null);

  const ariaLabel = getGroupQAriaLabel(
    screenReaderLabelQuestionId,
    questionsArray,
    pageId,
    isEditMode,
    listIndex,
  );

  const timeStampRef = useRef(null);
  const {
    question_id: questionId,
    question_type: questionType,
    question_text: questionText,
    display_type: displayType,
    question_description: questionDescription,
    child_questions: hasChildQuestions,
    hint_title: tooltipTitle,
    hint_text: tooltipBody,
    hint_html: tooltipHTML,
    tooltip_placement: tooltipPlacement,
    reflexive_question_flag: isReflexiveQuestion,
    transaction_log: transactionLog,
    last_submitted_at: lastSubmittedAt,
    show_as_required: showAsRequired,
    response,
    action,
    tooltip,
    isControlledInput,
    styles,
    field_name: fieldName,
    sub_type: subType,
  } = question;

  // Tab Navigation
  const inputRef = useRef();
  const fakeInputRef = useRef();
  const [prevApiResponse, setPrevApiResponse] = useState('');

  useEffect(() => {
    const shouldFocus = lastFocusedElementId === questionId;
    const focusDiv = document.getElementById(lastFocusedElementId);
    if (
      inputRef &&
      inputRef.current &&
      shouldFocus &&
      typeof inputRef.current.focus === 'function'
    ) {
      inputRef.current.focus();
    } else if (meta.os === 'MacOS' && focusDiv && typeof focusDiv.focus === 'function') {
      focusDiv.focus();
    }
    // if (hasChildQuestions) {
    //   if (fakeInputRef && fakeInputRef.current && shouldFocus) {
    //     if (typeof fakeInputRef.current.focus === 'function') {
    //       fakeInputRef.current.focus();
    //     }
    //   }
    // }
  }, []);

  useEffect(() => {
    if (isReflexiveQuestion) {
      setPrevApiResponse(response);
    }
    updateTimeStamp(
      questionId,
      { transactionLog, lastSubmittedAt },
      true,
      question?.validations,
      response,
    );
  }, []);

  const tooltipProps = getTooltipProps(tooltipTitle, tooltipBody, tooltipHTML || tooltip);

  const tooltipPopperOffsetLeftPos = get(
    labelProps,
    'themeConfiguration.tooltipPopperOffsetLeftPos',
    0,
  );

  const validators = getValidators(question, currentSectionAnswerList, currentSectionQuestionList);
  const roundUp = get(question, 'validations.round_up', null);

  const getMinMaxValues = () => {
    const { validations } = question;
    const { min_value: minValue, max_value: maxValue } = validations;
    if (minValue && maxValue) {
      return { min: minValue.value, max: maxValue.value };
    }
    return null;
  };

  const size = get(question, 'properties.size.value', '');
  const isInline = get(question, 'properties.inline.value', '');
  const { validations } = question;
  const { auto_complete: autoComplete } = validations;
  // CSS Variables
  const cssVariables = getCSSVariables(theme);
  const _font = at(cssVariables, ['pageOverride.font', 'global.font']);

  const getThemeConfiguration = () => {
    const labelConfigs = get(labelProps, 'themeConfiguration', {});
    const font = _font?.[0]?.['input-labelText'] || _font?.[1]?.['input-labelText'];

    return {
      ...labelConfigs,
      ...themeConfig,
      font,
    };
  };
  const sliderTextFont = _font?.[0]?.['input-slider-text'] || _font?.[1]?.['input-slider-text'];
  const descriptionFont =
    _font?.[0]?.['input-slider-description'] || _font?.[1]?.['input-slider-description'];
  const themeC = get(labelProps, 'themeConfiguration', {});
  const inputFont = _font?.[0]?.['input-label'] || _font?.[1]?.['input-label'];
  const placeholderFont = _font?.[0]?.['input-placeholder'] || _font?.[1]?.['input-placeholder'];
  const tooltipColor = get(theme, 'config.theme.global.toolTip.color', '');
  const tooltipFont = get(theme, 'config.theme.global.toolTip.font', '');

  const handleBlur = (e, rawValue) => {
    const { value, dataset } = e.target;
    const hasError = dataset.error === 'true';
    if (maskChar && rawValue) {
      const isMasked = rawValue && rawValue.includes(maskChar);
      if (isMasked) {
        const appendedValue = rawValue
          .split('')
          .map((v, i) => (v === maskChar ? actualMaskRef.current[i] : v))
          .join('');
        actualMaskRef.current = appendedValue;
      } else {
        actualMaskRef.current = rawValue;
      }
    } else if (!rawValue) {
      actualMaskRef.current = '';
    }

    let preventReflexiveCall = value === prevApiResponse || hasError;
    const isReflexive = get(question, 'validations.required.is_reflexive', false);
    if (isReflexive) {
      preventReflexiveCall = false;
    }

    // !(prevResponse && value !== prevResponse || ((value === prevResponseRef.current)))
    if (!(value === prevResponseRef.current) || roundUp) {
      prevResponseRef.current = value;
      handleChange(
        value,
        true,
        {
          hasMask: mask,
          value: actualMaskRef.current,
        },
        {
          error: hasError,
        },
        preventReflexiveCall,
      );
    }
    updateTimeStamp(
      questionId,
      getTimeStampData(timeStampRef, transactionLog, meta),
      undefined,
      validators,
      value,
    );
    // updateAnswerArrayInSocket(questionId, isReflexiveQuestion);
  };

  const _handleChangeDebounce = debounce(handleChange, 100);

  const handleUSPSZip = async (value) => {
    dispatchUspsStart(sectionIndex);
    const data = await getCityStateLookup(value);
    dispatchUspsComplete();

    const { CITY, STATE } = uspsQuestions || {};
    if (!!CITY && !!STATE && !!data) {
      dispatchMultipleActions(
        Object.entries({
          [CITY.questionId]: data.city,
          [STATE.questionId]: data.state,
        }),
      );
    }
  };

  const onChangeHandler = (
    value,
    hasEdit,
    maskObj,
    hasError,
    preventReflexiveCall = true,
    ignoreChildQuestionsOn,
    shouldDebounce = true,
  ) => {
    const args = [
      value,
      hasEdit,
      maskObj,
      hasError,
      preventReflexiveCall,
      ignoreChildQuestionsOn,
      shouldDebounce,
    ];
    if (fieldName === 'ZIPCODE' && subType === 'usps' && !hasError.error) {
      handleUSPSZip(value);
    }
    if (shouldDebounce && !isControlledInput) {
      _handleChangeDebounce(...args);
    } else {
      handleChange(...args);
    }
  };

  const handleOnFocus = (e) => {
    const element = e.target;
    scrollQuestionIntoView({
      element,
      theme,
      role,
      screenViewport,
    });
    timeStampRef.current.timeStart = getUTCTimestamp();
  };

  if (questionType === 'text' || questionType === 'password' || questionType === 'text_area') {
    return (
      <InputWrapper
        name='input-wrapper'
        questionId={ questionId }
        styles={ styles }
        device={ screenViewport }
        ref={ timeStampRef }
      >
        <TextInput
          autoComplete={ autoComplete?.value }
          inputRef={ inputRef }
          labelText={ `${questionText} ${optionalText}`.trim() }
          id={ questionId }
          type={ questionType }
          onChange={ (value, err) => {
            onChangeHandler(value, true, { hasMask: mask }, err);
          } }
          onBlur={ handleBlur }
          ariaLabel={ ariaLabel }
          onFocus={ handleOnFocus }
          onTooltipFocus={ handleOnFocus }
          color={ colorScheme }
          value={ response }
          displayType={ displayType }
          isControlledInput={ isControlledInput }
          { ...labelProps }
          width={ getWidth('input', pageId, pageOverrides, size, screenViewport) }
          { ...validators }
          { ...tooltipProps }
          errorMessageColor={ errorMessageColor }
          actualThreshold={ actualThreshold }
          labelPosition={ labelPosition }
          tooltipPlacement={ tooltipPlacement }
          themeConfiguration={ { ...themeC, font: inputFont, placeholderFont } }
          labelStyles={ { style: { fontWeight: 400 } } }
          tooltipColor={ tooltipColor }
          tooltipFont={ tooltipFont }
          tooltipAriaLabel={ `${questionText} info dialog` }
          removeAriaLabelforPopper
          tooltipPopperOffsetLeftPos={ tooltipPopperOffsetLeftPos }
          forceTriggerValidation={ triggerValidation.includes(questionId) }
          isInline={ isInline }
          showOptionalText={ showOptionalText && !isQuotePage() }
          isWcagOptional={ !(showAsRequired || question?.validations?.required?.value) }
          isPreTextAriaHidden
        />
        {action && (
          <ActionButtons
            label={ action.label }
            actionProps={ action }
            sectionIndex={ sectionIndex }
            pageId={ pageId }
            value={ inputRef.current?.value }
            { ...buttonLabelProps }
          />
        )}
        {/* {hasChildQuestions && <FakeInput ref={ fakeInputRef } />} */}
      </InputWrapper>
    );
  }

  if (displayType === 'slider') {
    return (
      <>
        {questionText && (
          <QuestionText
            id={ `slider-label-${questionId}` }
            color={ questionTextColor }
            font={ sliderTextFont }
          >
            <span
              dangerouslySetInnerHTML={ {
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                __html: validateInnerHtml(`${questionText} ${optionalText}`.trim()),
              } }
            />
          </QuestionText>
        )}
        {questionDescription && (
          <QuestionDescription
            id={ `slider-desc-${questionId}` }
            color={ questionDescriptionColor }
            font={ descriptionFont }
          >
            <span
              dangerouslySetInnerHTML={ {
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                __html: validateInnerHtml(questionDescription),
              } }
            />
          </QuestionDescription>
        )}
        <Wrapper
          id={ `slider-field-${questionId}` }
          name='range-slider-wrapper'
          { ...labelProps.rangeSlider }
          ref={ timeStampRef }
        >
          <RangeSlider
            onChange={ (v) => {
              onChangeHandler(v, true, { hasMask: mask }, {}, false, true);
            } }
            defaultSelected={ parseInt(response, 10) }
            prefix='$'
            showOnlyStepMultiples={
              labelProps.rangeSlider ? labelProps.rangeSlider.showOnlyStepMultiples : false
            }
            { ...labelProps.rangeSlider }
            { ...validators }
            customLabelStyles={ sliderLabelStyles }
            hasCurrencyAbbrivation
            showValueToLeft
            haveCenterValueItem
          />
        </Wrapper>
        {hasChildQuestions && <FakeInput ref={ fakeInputRef } />}
      </>
    );
  }
  return (
    <InputWrapper
      name='input-wrapper'
      questionId={ questionId }
      styles={ styles }
      device={ screenViewport }
      ref={ timeStampRef }
    >
      <NumberInput
        inputRef={ inputRef }
        labelText={ `${questionText} ${optionalText}`.trim() }
        id={ questionId }
        onChange={ (value, err) => {
          onChangeHandler(value, true, { hasMask: mask }, err);
        } }
        onBlur={ handleBlur }
        onFocus={ handleOnFocus }
        onTooltipFocus={ handleOnFocus }
        value={ response }
        color={ colorScheme }
        { ...labelProps }
        width={ getWidth('input', pageId, pageOverrides, size, screenViewport) }
        { ...validators }
        { ...tooltipProps }
        errorMessageColor={ errorMessageColor }
        labelPosition={ labelPosition }
        tooltipPlacement={ tooltipPlacement }
        tooltipColor={ tooltipColor }
        tooltipFont={ tooltipFont }
        tooltipAriaLabel={ `${questionText} info dialog` }
        showOptionalText={ showOptionalText && !isQuotePage() }
        isWcagOptional={ !(showAsRequired || question?.validations?.required?.value) }
        { ...getMinMaxValues() }
        themeConfiguration={ {
          ...getThemeConfiguration(),
          font: inputFont,
          placeholderFont,
        } }
        tooltipPopperOffsetLeftPos={ tooltipPopperOffsetLeftPos }
        isPreTextAriaHidden
      />
      {/* {hasChildQuestions && <FakeInput ref={ fakeInputRef } />} */}
    </InputWrapper>
  );
};

Input.defaultProps = {
  sectionIndex: -1,
};
Input.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  /* eslint-disable react/no-unused-prop-types */
  sectionIndex: PropTypes.number,
  handleChange: PropTypes.func.isRequired,
};

const _handleChange = (
  props,
  response,
  hasEdit,
  maskObj = { hasMask: false, value: '' },
  hasError,
  preventReflexiveCall,
  ignoreChildQuestionsOn,
  dispatch,
) => {
  const questionId = get(props, 'question.question_id', '');
  const auditRequired = get(props, 'question.audit_required', false);
  const transactionLog = get(props, 'question.transaction_log', []);

  dispatch(
    updateAnswersArray({
      questionId,
      response,
      hasEdit,
      hasMask: maskObj.hasMask,
      actualResponse: maskObj.value,
      sectionIndex: props.sectionIndex,
      preventReflexiveCall,
      isError: hasError.error,
      ignoreChildQuestionsOn,
      propQuestion: props.question,
      listParentId: props.listParentId,
      listIndex: props.listIndex,
      auditRequired,
      isNestedQ: props.isNestedQ,
      parentListIndex: props.parentListIndex,
      transactionLog,
    }),
  );
};

const mapStateToProps = (state, props) => {
  const { questions, meta } = state;
  const {
    sections,
    activeSubsectionId,
    // sectionIndex,
    lastFocusedElementId,
    uspsConfig,
  } = questions;
  const { uspsQuestions } = uspsConfig || {};
  const { screenViewport } = meta;
  const role = get(state, 'questions.role', '');
  const { sectionIndex } = props;
  const questionsArray = get(sections[activeSubsectionId], `[${sectionIndex}].questionList`, []);

  // const answersArray = get(sections[activeSubsectionId], `[${sectionIndex}].answersArray`, []);
  // const prevResponse = get(
  //   answersArray.find((a) => a.question_id === props?.question?.question_id),
  //   'answer',
  //   '',
  // );

  const currentSectionAnswerList = get(
    sections,
    `${activeSubsectionId}[${props.sectionIndex}].answersArray`,
    [],
  );
  const currentSectionQuestionList = get(
    sections,
    `${activeSubsectionId}[${props.sectionIndex}].questionList`,
    [],
  );
  const triggerValidation = get(questions, 'triggerValidation', []);
  return {
    // prevResponse,
    lastFocusedElementId,
    triggerValidation,
    role,
    screenViewport,
    uspsQuestions,
    currentSectionAnswerList,
    currentSectionQuestionList,
    meta: state.meta,
    questionsArray,
    listIndex: props.listIndex,
  };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
    handleChange: (
      response,
      hasEdit,
      maskObj,
      hasError,
      preventReflexiveCall,
      ignoreChildQuestionsOn,
    ) => {
      _handleChange(
        props,
        response,
        hasEdit,
        maskObj,
        hasError,
        preventReflexiveCall,
        ignoreChildQuestionsOn,
        dispatch,
      );
    },
    dispatchMultipleActions: (
      questionsArray = [],
      preventReflexiveCall = false,
      ignoreChildQuestionsOn,
    ) => {
      questionsArray.map((que) => {
        let isControlledInput = true;
        if (que[0].toLowerCase().includes('state')) isControlledInput = false;
        const triggerValidation = get(props, 'question.validations.trigger_validation.value');
        return dispatch(
          updateAnswersArray({
            questionId: que[0],
            response: que[1],
            triggerValidation,
            sectionIndex: props.sectionIndex,
            preventReflexiveCall,
            isControlledInput,
            ignoreChildQuestionsOn,
          }),
        );
      });
    },
    dispatchUspsStart: (sectionIndex) => {
      dispatch({
        type: USPS_API_START,
        payload: {
          sectionIndex,
        },
      });
    },
    dispatchUspsComplete: () => {
      dispatch({
        type: USPS_API_COMPLETE,
      });
    },
    updateTimeStamp: (questionId, timeStampData, isPrefilled, validators, response) => {
      const isUpdateTimeStamp = !(validators?.mask && response);
      if (isUpdateTimeStamp)
        dispatch(
          updateQuestionAnswerTimeStamp(questionId, timeStampData, props.sectionIndex, isPrefilled),
        );
    },
    // updateAnswerArrayInSocket: (questionId, isReflexiveQuestion = false) => {
    //   dispatch(updateQuestionAnswerInSocket(questionId, isReflexiveQuestion));
    // },
  };
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(Input));
